// rounds/configuration types — step 4, « Communications » (Figma 02.8 · 334:27126, 374:13742).
import { z } from "zod";
import type { Locale } from "@core/locale";

/**
 * The locales a template ASSIGNMENT actually exists in — NOT `@core/locale`'s `LOCALES`
 * (`["fr","ar","en"]`, the CONTENT locale set every round's text fields still use).
 *
 * ⚠ DZCH-138: current API locale inputs are limited to `fr` and `en` (docs/api/acquire-frontend-
 * integration.md, "API locale policy") — sending `ar` in a template-assignment request or expecting
 * an `ar` row from `GET /template-catalog/` is refused/never returned. `toTemplateChoices` used to
 * require all THREE `LOCALES` for `isComplete`, so `idByLocale.ar` was always absent and every
 * template — however completely seeded — read as incomplete. Every contact point then showed the
 * "template unavailable" warning permanently, which is what "email templates are missing" looked
 * like to an administrator staring at a fully-configured round.
 */
export const TEMPLATE_LOCALES: readonly Locale[] = ["fr", "en"];

/**
 * The template slots the backend models (`RoundTemplateAssignment.Slot`).
 *
 * Twelve exist; the design surfaces EIGHT. The four it leaves out — `application_reminder`,
 * `missing_documents`, `deadline_reminder`'s twin `closing_reminder`, and `transferred_to_select` —
 * are not dead: readiness treats an absent slot as "not an enabled contact point", so omitting them
 * costs nothing and adding a row later needs no migration.
 */
export const TEMPLATE_SLOTS = [
  "application_started",
  "application_reminder",
  "missing_documents",
  "submission_confirmation",
  "deadline_reminder",
  "information_requested",
  "inactivity_reminder",
  "closing_reminder",
  "review_in_progress",
  "deadline_changed",
  "intake_closed",
  "transferred_to_select",
] as const;

export type TemplateSlot = (typeof TEMPLATE_SLOTS)[number];

/**
 * The three slots readiness requires ASSIGNED IN EVERY LOCALE before a round can be scheduled
 * (`AUTOMATED_TEMPLATE_SLOTS` in the backend's `services.py`).
 *
 * ⚠ THE BASELINE IS SIX ASSIGNMENTS, not three — three slots × fr/en (see {@link TEMPLATE_LOCALES}).
 * And "required" has an escape hatch: readiness subtracts any assignment carrying `enabled: false`,
 * which is why the design's banner says to disable a contact point EXPLICITLY rather than leaving it
 * untouched. Untouched is the one state that blocks publication.
 */
export const MANDATORY_SLOTS: readonly TemplateSlot[] = [
  "application_started",
  "submission_confirmation",
  "deadline_reminder",
];

export function isMandatorySlot(slot: TemplateSlot): boolean {
  return MANDATORY_SLOTS.includes(slot);
}

/**
 * The eight contact points the design lists, in its order.
 *
 * The order is the design's and it is not the enum's: it follows the CANDIDAT's timeline — getting
 * in, submitting, being asked for something, being chased, being warned, waiting, being told the
 * date moved, being told it is over. An administrator reading down the list is walking the
 * applicant's experience, which is the only order in which "is anything missing?" is answerable.
 */
export const CONTACT_POINTS: readonly TemplateSlot[] = [
  "application_started",
  "submission_confirmation",
  "information_requested",
  "inactivity_reminder",
  "deadline_reminder",
  "review_in_progress",
  "deadline_changed",
  "intake_closed",
];

/** i18n key per slot — `<slot>` for the name, `<slot>-help` for the line under it. */
export const SLOT_LABEL_KEYS = {
  application_started: "round-create.communications.slot.application-started",
  application_reminder: "round-create.communications.slot.application-reminder",
  missing_documents: "round-create.communications.slot.missing-documents",
  submission_confirmation:
    "round-create.communications.slot.submission-confirmation",
  deadline_reminder: "round-create.communications.slot.deadline-reminder",
  information_requested:
    "round-create.communications.slot.information-requested",
  inactivity_reminder: "round-create.communications.slot.inactivity-reminder",
  closing_reminder: "round-create.communications.slot.closing-reminder",
  review_in_progress: "round-create.communications.slot.review-in-progress",
  deadline_changed: "round-create.communications.slot.deadline-changed",
  intake_closed: "round-create.communications.slot.intake-closed",
  transferred_to_select:
    "round-create.communications.slot.transferred-to-select",
} as const satisfies Record<TemplateSlot, string>;

export const SLOT_HELP_KEYS = {
  application_started:
    "round-create.communications.slot.application-started-help",
  application_reminder:
    "round-create.communications.slot.application-reminder-help",
  missing_documents: "round-create.communications.slot.missing-documents-help",
  submission_confirmation:
    "round-create.communications.slot.submission-confirmation-help",
  deadline_reminder: "round-create.communications.slot.deadline-reminder-help",
  information_requested:
    "round-create.communications.slot.information-requested-help",
  inactivity_reminder:
    "round-create.communications.slot.inactivity-reminder-help",
  closing_reminder: "round-create.communications.slot.closing-reminder-help",
  review_in_progress:
    "round-create.communications.slot.review-in-progress-help",
  deadline_changed: "round-create.communications.slot.deadline-changed-help",
  intake_closed: "round-create.communications.slot.intake-closed-help",
  transferred_to_select:
    "round-create.communications.slot.transferred-to-select-help",
} as const satisfies Record<TemplateSlot, string>;

// ── The template catalogue (`GET /template-catalog/`) ────────────────────────────────────────

/** One row of `GET /acquire/template-catalog/` — an active EMAIL `NotificationTemplate`. */
export const TemplateCatalogueRowSchema = z.object({
  id: z.number().int(),
  key: z.string(),
  locale: z.string(),
  version: z.number().int(),
  channel: z.string(),
});

export type TemplateCatalogueRow = z.infer<typeof TemplateCatalogueRowSchema>;

/**
 * A catalogue template as the UI offers it: ONE choice, three locale variants behind it.
 *
 * ⚠ THIS IS THE STEP'S CENTRAL SHAPE, and it exists because the contract and the design disagree
 * about what a "choice" is. The backend keys an assignment by (slot, LOCALE) — so a slot needs
 * three rows, one per locale, each pointing at a template whose own `locale` matches
 * (`assignment.notification_template.locale == assignment.locale`, enforced in readiness). The
 * design shows ONE select per contact point.
 *
 * Both are right. A `NotificationTemplate` is one message in three languages stored as three rows
 * sharing a `key`, so the administrator's real choice is the KEY — picking « Lien de connexion »
 * means all three variants of it. So the select offers keys, and one selection writes three
 * assignments. This also keeps the interface free of Arabic while Arabic is still sent as content,
 * which is the standing i18n position.
 */
export interface TemplateChoice {
  key: string;
  /** Template id per locale. A key missing a locale cannot satisfy readiness — see `isComplete`. */
  idByLocale: Partial<Record<Locale, number>>;
  /** True when both {@link TEMPLATE_LOCALES} are present, i.e. this key can actually be assigned. */
  isComplete: boolean;
}

/**
 * Fold the flat catalogue rows into one choice per `key`.
 *
 * Incomplete keys are KEPT rather than filtered, and flagged instead. A template seeded in French
 * only is a real thing an administrator may see in the list; hiding it would present the select as
 * complete and let readiness fail later with `templates_assigned` naming a locale the UI never
 * mentioned.
 */
export function toTemplateChoices(
  rows: readonly TemplateCatalogueRow[]
): TemplateChoice[] {
  const byKey = new Map<string, Partial<Record<Locale, number>>>();

  for (const row of rows) {
    // A row outside TEMPLATE_LOCALES should never arrive — the API only ever seeds/returns fr/en —
    // but this is the defensive read boundary rather than an assumption to trust blindly.
    const locale = TEMPLATE_LOCALES.find((item) => item === row.locale);
    if (!locale) continue;
    const existing = byKey.get(row.key) ?? {};
    // Highest version wins, since the catalogue may hold more than one per (key, locale).
    const current = existing[locale];
    if (current === undefined || row.id > current) existing[locale] = row.id;
    byKey.set(row.key, existing);
  }

  return [...byKey.entries()].map(([key, idByLocale]) => ({
    key,
    idByLocale,
    isComplete: TEMPLATE_LOCALES.every(
      (locale) => idByLocale[locale] !== undefined
    ),
  }));
}

/**
 * The template key the backend seeds for a slot (`acquire.<slot>`, from
 * `seed_acquire_configuration`).
 *
 * Used only to PRESELECT, never to assume: the select lists whatever the catalogue returns, and a
 * slot whose conventional key is absent simply starts empty.
 */
export function conventionalTemplateKey(slot: TemplateSlot): string {
  return `acquire.${slot}`;
}

// ── The draft ────────────────────────────────────────────────────────────────────────────────

/**
 * One contact point as the administrator configures it.
 *
 * `template_key`, not a template id — the id is per locale, and which three ids a key resolves to
 * is a function of the catalogue at SAVE time, not at pick time. Storing ids in a draft that
 * survives a reload would pin versions that may have been superseded.
 */
export const ContactPointDraftSchema = z.object({
  slot: z.enum(TEMPLATE_SLOTS),
  is_enabled: z.boolean(),
  template_key: z.string(),
});

export type ContactPointDraft = z.infer<typeof ContactPointDraftSchema>;

/**
 * Step 4's whole draft: the round-level sender identity plus the eight contact points.
 *
 * In the CLIENT draft for the same reason documents, competencies and questions are — `PATCH
 * /communications/` and `PUT /template-assignments/…` both need a round, and step 1 no longer
 * creates one. Persists across a reload with the rest of the draft.
 */
export const RoundCommunicationsDraftSchema = z.object({
  sender_name: z.string().max(255),
  reply_to_email: z.union([z.literal(""), z.string().email()]),
  recipient_email: z.union([z.literal(""), z.string().email()]),
  contact_points: z.array(ContactPointDraftSchema),
});

export type RoundCommunicationsDraft = z.infer<
  typeof RoundCommunicationsDraftSchema
>;

/**
 * The eight design rows, each with a selectable template assignment. The draft stores a stable
 * template key; locale-specific template IDs are resolved from the catalogue only when saving.
 */
export function defaultContactPoints(): ContactPointDraft[] {
  return CONTACT_POINTS.map((slot) => ({
    slot,
    // Enabled by default because that is the design's state for seven of the eight rows, and
    // because a contact point is the platform doing its job — silence is the deliberate choice.
    // `deadline_changed` ships OFF to match the design, which uses it to show the disabled state.
    is_enabled: slot !== "deadline_changed",
    template_key: conventionalTemplateKey(slot),
  }));
}

export function emptyCommunicationsDraft(): RoundCommunicationsDraft {
  return {
    sender_name: "",
    reply_to_email: "",
    recipient_email: "",
    contact_points: defaultContactPoints(),
  };
}

// ── Readiness prediction ─────────────────────────────────────────────────────────────────────

/**
 * The contact points that would block scheduling: enabled, but with no usable template.
 *
 * Predicted client-side so the warning lands on the row that causes it, next to the control that
 * fixes it. The authority is still `GET /readiness/` — this only says the same thing earlier, and
 * says it in the one place where "which row?" is answerable.
 *
 * Two ways to be blocking, and they read identically to `templates_assigned`:
 *   - enabled with NO key picked
 *   - enabled with a key that is missing a locale (so no valid (slot, locale) triple exists)
 */
export function blockingContactPoints(
  points: readonly ContactPointDraft[],
  choices: readonly TemplateChoice[]
): ContactPointDraft[] {
  return points.filter((point) => {
    if (!point.is_enabled) return false;
    if (!point.template_key) return true;
    const choice = choices.find((item) => item.key === point.template_key);
    return !choice?.isComplete;
  });
}

/** True when exactly one of the two sender addresses is filled. */
export function hasPartialSenderIdentity(
  draft: Pick<RoundCommunicationsDraft, "reply_to_email" | "recipient_email">
): boolean {
  const hasReplyTo = draft.reply_to_email.trim().length > 0;
  const hasRecipient = draft.recipient_email.trim().length > 0;
  return hasReplyTo !== hasRecipient;
}
